import { useEffect, useRef, useState } from "react";
import { GripVertical } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../shadcn/tooltip";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import {
  AI_CHAT_TOGGLE_EVENT,
  CHAT_OPEN_STATE_KEY,
  CHAT_VIEW_MODE_KEY,
  type ChatHeaderViewMode,
  type ChatViewMode,
} from "./chatConstants";
import { openFullPageChat } from "./chatNavigation";
import { ChatHeader, ChatInput, ChatMessages } from "./AiChatView";
import { ChatLengthStatus, chatLengthIndicator } from "./ChatLengthStatus";
import { useAiChatSession } from "./useAiChatSession";
import { useConfirmNewChat } from "./ConfirmNewChat";

// Side toggle button
const ChatToggle = ({ isOpen, onClick }: { isOpen: boolean; onClick: () => void }) =>
  !isOpen && (
    <button
      type="button"
      onClick={onClick}
      className="absolute top-4 right-full rounded-l-md border border-r-0 border-slate-200 bg-white px-1 py-1 text-xs font-semibold text-slate-700 shadow-sm"
      aria-expanded={isOpen}
      aria-controls="chat-body"
    >
      Ask Vecta
    </button>
  );

const CHAT_WIDTH_KEY = "ai-chat-panel-width";
const CHAT_MIN_WIDTH = 360;
const CHAT_MAX_WIDTH = 640;
const CHAT_DEFAULT_WIDTH = 448;

const clampChatWidth = (width: number) =>
  Math.min(CHAT_MAX_WIDTH, Math.max(CHAT_MIN_WIDTH, Math.round(width)));

const RESIZE_STEP = 16;
const RESIZE_STEP_LARGE = 64;
const RESIZE_TOOLTIP = "Drag to resize. Double-click to reset.";

const ResizeTooltip = ({ open, children }: { open: boolean; children: React.ReactNode }) => (
  <TooltipProvider delayDuration={400}>
    <Tooltip open={open ? undefined : false}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="left" className="px-2 py-1 text-xs">
        {RESIZE_TOOLTIP}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

// Tracks a pointer drag on a handle and toggles the page-wide resizing class
const useResizeDrag = (onMove: (event: PointerEvent) => void) => {
  const [dragging, setDragging] = useState(false);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const handle = e.currentTarget;
    handle.setPointerCapture(e.pointerId);
    document.documentElement.classList.add("ai-chat-resizing");
    setDragging(true);

    const handleUp = () => {
      document.documentElement.classList.remove("ai-chat-resizing");
      setDragging(false);
      handle.removeEventListener("pointermove", onMove);
      handle.removeEventListener("pointerup", handleUp);
      handle.removeEventListener("pointercancel", handleUp);
    };

    handle.addEventListener("pointermove", onMove);
    handle.addEventListener("pointerup", handleUp);
    handle.addEventListener("pointercancel", handleUp);
  };

  return { dragging, handlePointerDown };
};

const stepFor = (event: React.KeyboardEvent) => (event.shiftKey ? RESIZE_STEP_LARGE : RESIZE_STEP);

// Drag handle on the panel's left edge
const ChatResizeHandle = ({
  width,
  onResize,
  onReset,
}: {
  width: number;
  onResize: (width: number) => void;
  onReset: () => void;
}) => {
  const { dragging, handlePointerDown } = useResizeDrag((moveEvent) =>
    onResize(clampChatWidth(window.innerWidth - moveEvent.clientX))
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const next: Record<string, number | undefined> = {
      ArrowLeft: width + stepFor(event),
      ArrowRight: width - stepFor(event),
      Home: CHAT_MIN_WIDTH,
      End: CHAT_MAX_WIDTH,
    };
    if (event.key === "Enter") {
      event.preventDefault();
      onReset();
      return;
    }
    const target = next[event.key];
    if (target === undefined) return;
    event.preventDefault();
    onResize(clampChatWidth(target));
  };

  return (
    <ResizeTooltip open={!dragging}>
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize chat panel"
        aria-controls="chat-body"
        aria-valuenow={width}
        aria-valuemin={CHAT_MIN_WIDTH}
        aria-valuemax={CHAT_MAX_WIDTH}
        tabIndex={0}
        onPointerDown={handlePointerDown}
        onDoubleClick={onReset}
        onKeyDown={handleKeyDown}
        data-dragging={dragging || undefined}
        className="group absolute inset-y-0 -left-1 z-10 w-2 cursor-col-resize max-lg:hidden touch-none outline-none"
      >
        <span
          aria-hidden="true"
          className={`absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 transition-colors group-hover:bg-blue-500 group-focus-visible:bg-blue-500 ${
            dragging ? "bg-blue-500" : "bg-transparent"
          }`}
        />
        <span
          aria-hidden="true"
          className={`absolute top-1/2 left-1/2 flex h-10 w-3 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border bg-white shadow-sm transition-colors group-hover:border-blue-500 group-hover:text-blue-500 group-focus-visible:border-blue-500 group-focus-visible:text-blue-500 ${
            dragging ? "border-blue-500 text-blue-500" : "border-slate-300 text-slate-400"
          }`}
        >
          <GripVertical className="size-3" />
        </span>
      </div>
    </ResizeTooltip>
  );
};

type FloatingSize = { width: number; height: number };
type FloatingEdge = "left" | "top" | "top-left";

const CHAT_FLOATING_SIZE_KEY = "ai-chat-panel-floating-size";
const FLOATING_DEFAULT_SIZE: FloatingSize = { width: 416, height: 640 };
const FLOATING_MIN_SIZE: FloatingSize = { width: 360, height: 420 };
const FLOATING_MAX_SIZE: FloatingSize = { width: 640, height: 900 };
const FLOATING_OFFSET = 16;

const clampFloatingSize = ({ width, height }: FloatingSize): FloatingSize => ({
  width: Math.round(Math.min(FLOATING_MAX_SIZE.width, Math.max(FLOATING_MIN_SIZE.width, width))),
  height: Math.round(Math.min(FLOATING_MAX_SIZE.height, Math.max(FLOATING_MIN_SIZE.height, height))),
});

const FLOATING_HANDLE_CLASS: Record<FloatingEdge, string> = {
  left: "inset-y-3 left-0 w-1.5 cursor-ew-resize",
  top: "inset-x-3 top-0 h-1.5 cursor-ns-resize",
  "top-left": "top-0 left-0 size-4 cursor-nwse-resize",
};

const FLOATING_LINE_CLASS: Record<"left" | "top", string> = {
  left: "inset-y-0 left-0 w-0.5",
  top: "inset-x-0 top-0 h-0.5",
};

// Drag handles on the floating panel's top and left edges (it is anchored bottom-right)
const FloatingResizeHandle = ({
  edge,
  onResize,
  onReset,
}: {
  edge: FloatingEdge;
  onResize: (size: Partial<FloatingSize>) => void;
  onReset: () => void;
}) => {
  const { dragging, handlePointerDown } = useResizeDrag((moveEvent) => {
    const width = window.innerWidth - FLOATING_OFFSET - moveEvent.clientX;
    const height = window.innerHeight - FLOATING_OFFSET - moveEvent.clientY;
    if (edge === "left") onResize({ width });
    else if (edge === "top") onResize({ height });
    else onResize({ width, height });
  });

  return (
    <div
      aria-hidden="true"
      onPointerDown={handlePointerDown}
      onDoubleClick={onReset}
      className={`group absolute z-10 touch-none max-lg:hidden ${FLOATING_HANDLE_CLASS[edge]}`}
    >
      {edge !== "top-left" && (
        <span
          className={`absolute transition-colors group-hover:bg-blue-500 ${FLOATING_LINE_CLASS[edge]} ${
            dragging ? "bg-blue-500" : "bg-transparent"
          }`}
        />
      )}
    </div>
  );
};

// Focusable corner grip: the visible, keyboard-operable resize control for the floating panel
const FloatingCornerGrip = ({
  size,
  onResize,
  onReset,
}: {
  size: FloatingSize;
  onResize: (size: Partial<FloatingSize>) => void;
  onReset: () => void;
}) => {
  const { dragging, handlePointerDown } = useResizeDrag((moveEvent) =>
    onResize({
      width: window.innerWidth - FLOATING_OFFSET - moveEvent.clientX,
      height: window.innerHeight - FLOATING_OFFSET - moveEvent.clientY,
    })
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = stepFor(event);
    const changes: Record<string, Partial<FloatingSize> | undefined> = {
      ArrowLeft: { width: size.width + step },
      ArrowRight: { width: size.width - step },
      ArrowUp: { height: size.height + step },
      ArrowDown: { height: size.height - step },
      Home: FLOATING_MIN_SIZE,
      End: FLOATING_MAX_SIZE,
    };
    if (event.key === "Enter") {
      event.preventDefault();
      onReset();
      return;
    }
    const change = changes[event.key];
    if (!change) return;
    event.preventDefault();
    onResize(change);
  };

  return (
    <ResizeTooltip open={!dragging}>
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize chat window"
        aria-valuenow={size.width}
        aria-valuemin={FLOATING_MIN_SIZE.width}
        aria-valuemax={FLOATING_MAX_SIZE.width}
        aria-valuetext={`${size.width} by ${size.height} pixels`}
        tabIndex={0}
        onPointerDown={handlePointerDown}
        onDoubleClick={onReset}
        onKeyDown={handleKeyDown}
        className={`group absolute top-1 left-1 z-20 flex size-5 cursor-nwse-resize items-center justify-center rounded-md outline-none transition-colors max-lg:hidden touch-none hover:text-blue-500 focus-visible:text-blue-500 focus-visible:ring-2 focus-visible:ring-blue-500 ${
          dragging ? "text-blue-500" : "text-slate-400"
        }`}
      >
        <svg aria-hidden="true" viewBox="0 0 10 10" className="size-3 fill-current">
          <path d="M0 0H10L0 10Z" />
        </svg>
      </div>
    </ResizeTooltip>
  );
};

export const AiChatPanel = ({ showToggle = true }: { showToggle?: boolean }) => {
  const { getCacheItem, updateCacheItem, removeCacheItem } = useLocalStorage();
  const rootRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  const [isOpen, setIsOpen] = useState<boolean>(
    () => getCacheItem<boolean>(CHAT_OPEN_STATE_KEY) ?? false
  );
  const previousOpenRef = useRef(isOpen);

  const session = useAiChatSession({ active: isOpen });
  const { requestNewChat, confirmDialog } = useConfirmNewChat({
    hasMessages: session.hasMessages,
    onNewChat: session.handleNewChat,
  });

  const [panelWidth, setPanelWidth] = useState<number | null>(
    () => getCacheItem<number>(CHAT_WIDTH_KEY) ?? null
  );

  const [viewMode, setViewMode] = useState<ChatViewMode>(
    () => getCacheItem<ChatViewMode>(CHAT_VIEW_MODE_KEY) ?? "sidebar"
  );
  const isFloating = viewMode === "floating";

  const [floatingSize, setFloatingSize] = useState<FloatingSize>(
    () => clampFloatingSize(getCacheItem<FloatingSize>(CHAT_FLOATING_SIZE_KEY) ?? FLOATING_DEFAULT_SIZE)
  );

  const handleResetSize = () => {
    setPanelWidth(null);
    removeCacheItem(CHAT_WIDTH_KEY);
    setFloatingSize(FLOATING_DEFAULT_SIZE);
  };

  const handleFloatingResize = (resize: Partial<FloatingSize>) =>
    setFloatingSize((current) => clampFloatingSize({ ...current, ...resize }));

  const handleViewModeChange = (mode: ChatHeaderViewMode) => {
    if (mode === "fullscreen") {
      openFullPageChat();
      return;
    }
    setViewMode(mode);
  };

  const handleSend = () => {
    setIsOpen(true);
    return session.handleSend();
  };

  useEffect(() => {
    updateCacheItem<FloatingSize>(CHAT_FLOATING_SIZE_KEY, floatingSize);
  }, [floatingSize, updateCacheItem]);

  useEffect(() => {
    updateCacheItem<boolean>(CHAT_OPEN_STATE_KEY, isOpen);
  }, [isOpen, updateCacheItem]);

  useEffect(() => {
    updateCacheItem<ChatViewMode>(CHAT_VIEW_MODE_KEY, viewMode);
  }, [viewMode, updateCacheItem]);

  useEffect(() => {
    if (panelWidth === null) {
      document.documentElement.style.removeProperty("--ai-chat-width");
      return;
    }
    const width = clampChatWidth(panelWidth);
    document.documentElement.style.setProperty("--ai-chat-width", `${width}px`);
    updateCacheItem<number>(CHAT_WIDTH_KEY, width);
  }, [panelWidth, updateCacheItem]);

  useEffect(() => {
    const handleToggle = () => setIsOpen((open) => !open);
    window.addEventListener(AI_CHAT_TOGGLE_EVENT, handleToggle);
    return () => window.removeEventListener(AI_CHAT_TOGGLE_EVENT, handleToggle);
  }, []);

  useEffect(() => {
    const isDocked = isOpen && !isFloating;
    document.documentElement.classList.toggle("ai-chat-open", isDocked);
    return () => document.documentElement.classList.remove("ai-chat-open");
  }, [isOpen, isFloating]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
    return () => cancelAnimationFrame(frame);
  }, [isOpen, isFloating, panelWidth]);

  useEffect(() => {
    const content = contentRef.current;
    const root = rootRef.current;
    if (!content || !root) return;

    const wasOpen = previousOpenRef.current;
    previousOpenRef.current = isOpen;
    content.inert = !isOpen;

    if (isOpen && !wasOpen) {
      const active = document.activeElement;
      openerRef.current = active instanceof HTMLElement && !root.contains(active) ? active : null;
      const frame = requestAnimationFrame(() => textareaRef.current?.focus());
      return () => cancelAnimationFrame(frame);
    }

    if (!isOpen && wasOpen) {
      const opener = openerRef.current;
      openerRef.current = null;
      const focusWasInside = root.contains(document.activeElement) || document.activeElement === document.body;
      if (focusWasInside && opener?.isConnected) {
        opener.focus();
      }
    }
  }, [isOpen]);

  const panelClassName = isFloating
    ? `fixed right-4 bottom-4 z-40 flex h-(--ai-chat-float-h) w-(--ai-chat-float-w) max-h-[calc(100dvh-var(--layout-top-bar-height)-2rem)] max-w-[calc(100vw-2rem)] max-lg:inset-x-0 max-lg:top-(--layout-top-bar-height) max-lg:bottom-0 max-lg:h-auto max-lg:w-auto max-lg:max-h-none max-lg:max-w-none max-lg:rounded-none max-lg:border-x-0 max-lg:border-b-0 max-lg:shadow-none flex-col overflow-hidden overscroll-contain rounded-2xl border border-slate-200 bg-[#F7F8F9] text-slate-800 shadow-2xl transition-[opacity,transform] duration-200 ${
        isOpen ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0"
      }`
    : `fixed top-(--layout-top-bar-height) right-0 bottom-0 z-15 flex w-(--ai-chat-width) max-w-full flex-col border-l max-lg:w-full max-lg:border-l-0 border-slate-200 bg-[#F7F8F9] text-slate-800 transition-transform duration-300 ${
        isOpen ? "translate-x-0" : "translate-x-full"
      }`;

  const handleRootKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Escape" || event.defaultPrevented || event.nativeEvent.isComposing) return;
    event.stopPropagation();
    setIsOpen(false);
  };

  return (
    <div
      ref={rootRef}
      id="ai-chat"
      role={isOpen ? (isFloating ? "dialog" : "complementary") : undefined}
      aria-label={isOpen ? "Ask Vecta" : undefined}
      onKeyDown={handleRootKeyDown}
      className={panelClassName}
      style={isFloating ? ({ "--ai-chat-float-w": `${floatingSize.width}px`, "--ai-chat-float-h": `${floatingSize.height}px` } as React.CSSProperties) : undefined}
    >
      {isOpen && !isFloating && (
        <ChatResizeHandle
          width={panelWidth === null ? CHAT_DEFAULT_WIDTH : clampChatWidth(panelWidth)}
          onResize={setPanelWidth}
          onReset={handleResetSize}
        />
      )}
      {isOpen && isFloating && (
        <>
          <FloatingResizeHandle edge="left" onResize={handleFloatingResize} onReset={handleResetSize} />
          <FloatingResizeHandle edge="top" onResize={handleFloatingResize} onReset={handleResetSize} />
          <FloatingResizeHandle edge="top-left" onResize={handleFloatingResize} onReset={handleResetSize} />
          <FloatingCornerGrip size={floatingSize} onResize={handleFloatingResize} onReset={handleResetSize} />
        </>
      )}
      {showToggle && !isFloating && <ChatToggle isOpen={isOpen} onClick={() => setIsOpen(true)} />}
      <div ref={contentRef} className="contents">
      <ChatHeader
        onClose={() => setIsOpen(false)}
        onNewChat={requestNewChat}
        viewMode={viewMode}
        onResetSize={handleResetSize}
        onViewModeChange={handleViewModeChange}
      />
      <div id="chat-body" className="flex flex-1 flex-col overflow-hidden px-3 pt-3 pb-5">
        <ChatMessages
          messages={session.messages}
          scrollRef={session.scrollRef}
          onScroll={session.handleMessagesScroll}
          showJumpToLatest={session.showJumpToLatest}
          onJumpToLatest={session.scrollToLatest}
          onPromptSelect={session.handleChatChanged}
          isSending={session.isSending}
          onRegenerate={session.handleRegenerate}
          onRetry={session.handleRetry}
          isRestoring={session.isLoadingHistory}
          editableMessageId={session.editableMessageId}
          onEditLast={() => {
            session.handleEditLast();
            textareaRef.current?.focus();
          }}
        />

        <ChatLengthStatus
          length={session.length}
          isSending={session.isSending}
          onCompact={session.handleCompact}
          onNewChat={requestNewChat}
        />
        <ChatInput
          limitReached={session.length.limitReached}
          lengthIndicator={chatLengthIndicator(session.length)}
          textareaRef={textareaRef}
          value={session.messageText}
          onChange={session.handleChatChanged}
          onSend={handleSend}
          onStop={session.handleStop}
          isSending={session.isSending}
          onNewChat={requestNewChat}
          editing={session.editing}
          onCancelEdit={session.cancelEdit}
          onEditLast={session.editableMessageId === null ? undefined : session.handleEditLast}
          disabled={!session.messageText.trim() || session.isSending || session.isLoadingHistory}
        />
      </div>
      </div>
      {confirmDialog}
      <div role="status" aria-live="polite" className="sr-only">
        {session.announcement}
      </div>
    </div>
  );
};